import type { LanguageCode } from './types.js';

/**
 * English is the complete source of truth. `MessageKey` is derived from it, so
 * call sites are typo-proof and every key is guaranteed to have an English
 * value. Other languages are `Partial` — any missing key falls back to English
 * (see `translate`), which lets translations ship incrementally.
 */
export const en = {
  pttClickToTalk: 'Click to Talk',
  pttUnmuteMicFirst: 'Unmute microphone first',
  accept: 'Accept',
  cancel: 'Cancel',
  listening: 'Listening...',
  enableAudioToContinue: 'Enable audio to continue',
  muteDealer: 'Mute Dealer',
  unmuteDealer: 'Unmute Dealer',
  waitingForAgent: 'Waiting for AI agent to connect...',
  dealerActive: 'Dealer Active',
  dealerAway: 'Dealer Away',
  textOnly: 'Text Only',
  serviceActive: 'Service: Active',
  serviceAway: 'Service: Away',
  serviceTextOnly: 'Service: Text Only',
  tapToEnableAudio: 'Tap to hear your dealer',
  tapToStart: 'Tap to start',
  placeBetToTalk: 'Place a bet to talk to the dealer',
  settingUpTable: 'Setting up your table',
  enableAudioStartChat: 'Enable Audio & Start Voice Chat',
  cancelHint: 'swipe up\nto cancel',
  retry: 'Retry',
  errorPrefix: 'Error:',
  micEnabling: 'Enabling mic…',
  micEnableInSettings: 'Enable mic in browser settings',
  micEnabledHoldToTalk: 'Mic enabled — hold to talk',
  micUnavailable: 'Voice chat unavailable',
  aiUnavailable: 'AI unavailable',
  messagePlaceholder: 'Type a message…',
  messageDealer: 'Message the dealer',
  sendMessage: 'Send message',
  dealerThinking: 'Dealer is thinking',
} as const;

export type MessageKey = keyof typeof en;

/** en-GB ≈ en for these strings; per-key fallback to en. Placeholder for future GB tone. */
const enGB: Partial<Record<MessageKey, string>> = {};

const esLatam: Partial<Record<MessageKey, string>> = {
  pttClickToTalk: 'Pulsa para hablar',
  pttUnmuteMicFirst: 'Activa primero el micrófono',
  accept: 'Aceptar',
  cancel: 'Cancelar',
  listening: 'Escuchando...',
  enableAudioToContinue: 'Activa el audio para continuar',
  muteDealer: 'Silenciar al crupier',
  unmuteDealer: 'Activar al crupier',
  waitingForAgent: 'Esperando la conexión del crupier IA...',
  dealerActive: 'Crupier activo',
  dealerAway: 'Crupier ausente',
  textOnly: 'Solo texto',
  serviceActive: 'Servicio: Activo',
  serviceAway: 'Servicio: Ausente',
  serviceTextOnly: 'Servicio: Solo texto',
  tapToEnableAudio: 'Toca para escuchar a tu crupier',
  tapToStart: 'Toca para empezar',
  placeBetToTalk: 'Haz una apuesta para hablar con el crupier',
  settingUpTable: 'Preparando tu mesa',
  enableAudioStartChat: 'Activar audio e iniciar chat de voz',
  cancelHint: 'desliza hacia arriba\npara cancelar',
  retry: 'Reintentar',
  errorPrefix: 'Error:',
  micEnabling: 'Activando micrófono…',
  micEnableInSettings: 'Activa el micrófono en los ajustes del navegador',
  micEnabledHoldToTalk: 'Micrófono activado — mantén pulsado para hablar',
  micUnavailable: 'Chat de voz no disponible',
  aiUnavailable: 'IA no disponible',
  messagePlaceholder: 'Escribe un mensaje…',
  messageDealer: 'Mensaje para el crupier',
  sendMessage: 'Enviar mensaje',
  dealerThinking: 'El crupier está pensando',
};

const ptBR: Partial<Record<MessageKey, string>> = {
  pttClickToTalk: 'Toque para falar',
  pttUnmuteMicFirst: 'Ative o microfone primeiro',
  accept: 'Aceitar',
  cancel: 'Cancelar',
  listening: 'Ouvindo...',
  enableAudioToContinue: 'Ative o áudio para continuar',
  muteDealer: 'Silenciar o dealer',
  unmuteDealer: 'Ativar o dealer',
  waitingForAgent: 'Aguardando a conexão do dealer de IA...',
  dealerActive: 'Dealer ativo',
  dealerAway: 'Dealer ausente',
  textOnly: 'Apenas texto',
  serviceActive: 'Serviço: Ativo',
  serviceAway: 'Serviço: Ausente',
  serviceTextOnly: 'Serviço: Apenas texto',
  tapToEnableAudio: 'Toque para ouvir seu dealer',
  tapToStart: 'Toque para começar',
  placeBetToTalk: 'Faça uma aposta para falar com o dealer',
  settingUpTable: 'Preparando sua mesa',
  enableAudioStartChat: 'Ativar áudio e iniciar chat de voz',
  cancelHint: 'deslize para cima\npara cancelar',
  retry: 'Tentar novamente',
  errorPrefix: 'Erro:',
  micEnabling: 'Ativando microfone…',
  micEnableInSettings: 'Ative o microfone nas configurações do navegador',
  micEnabledHoldToTalk: 'Microfone ativado — segure para falar',
  micUnavailable: 'Chat de voz indisponível',
  aiUnavailable: 'IA indisponível',
  messagePlaceholder: 'Digite uma mensagem…',
  messageDealer: 'Mensagem para o dealer',
  sendMessage: 'Enviar mensagem',
  dealerThinking: 'O dealer está pensando',
};

const tr: Partial<Record<MessageKey, string>> = {
  pttClickToTalk: 'Konuşmak için basılı tut',
  pttUnmuteMicFirst: 'Önce mikrofonu aç',
  accept: 'Kabul et',
  cancel: 'Vazgeç',
  listening: 'Dinliyorum...',
  enableAudioToContinue: 'Devam etmek için sesi aç',
  muteDealer: "Dealer'ı sessize al",
  unmuteDealer: "Dealer'ın sesini aç",
  waitingForAgent: "Yapay zeka dealer'ı bağlanıyor...",
  dealerActive: 'Dealer masada',
  dealerAway: 'Dealer masada değil',
  textOnly: 'Sadece metin',
  serviceActive: 'Servis: Aktif',
  serviceAway: 'Servis: Müsait değil',
  serviceTextOnly: 'Servis: Sadece metin',
  tapToEnableAudio: "Dealer'ını duymak için dokun",
  tapToStart: 'Başlamak için dokun',
  placeBetToTalk: "Dealer'la konuşmak için bahis yap",
  settingUpTable: 'Masan hazırlanıyor',
  enableAudioStartChat: 'Sesi aç, sohbete başla',
  cancelHint: 'vazgeçmek için\nyukarı kaydır',
  retry: 'Tekrar dene',
  errorPrefix: 'Hata:',
  micEnabling: 'Mikrofon açılıyor…',
  micEnableInSettings: 'Mikrofonu tarayıcı ayarlarından aç',
  micEnabledHoldToTalk: 'Mikrofon açık, konuşmak için basılı tut',
  micUnavailable: 'Sesli sohbet kullanılamıyor',
  aiUnavailable: 'Yapay zekaya ulaşılamıyor',
  messagePlaceholder: 'Bir mesaj yaz…',
  messageDealer: "Dealer'a mesaj yaz",
  sendMessage: 'Mesajı gönder',
  dealerThinking: 'Dealer düşünüyor',
};

export const catalog: Record<LanguageCode, Partial<Record<MessageKey, string>>> = {
  en,
  'en-GB': enGB,
  'es-419': esLatam,
  'pt-BR': ptBR,
  tr,
};
